
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FilePlus, LibraryBig } from "lucide-react";
import { ResourceFormFields } from "./resource/ResourceFormFields";
import { useResourceCreation } from "./resource/useResourceCreation";
import { ResourceFormData } from "./resource/types";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { useSession } from "@/hooks/useSession";
import { getMaxResources } from "@/utils/subscriptionLimits";
import { Alert, AlertDescription } from "./ui/alert";
import { CourseLibraryDialog } from "./resource/CourseLibraryDialog";
import type { SubscriptionTier } from "@/hooks/useProfile";

export function CreateResourceDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const { createResource, isLoading } = useResourceCreation();
  const { toast } = useToast();
  const { session } = useSession();
  const { data: profile } = useProfile(session?.user?.id);
  
  const {
    register,
    handleSubmit,
    reset,
  } = useForm<ResourceFormData>({
    defaultValues: {
      primaryColor: "#9b87f5",
      secondaryColor: "#7E69AB",
    }
  });

  const subscriptionTier = (profile?.subscription_tier === 'trainee' ? 'free' : profile?.subscription_tier) as SubscriptionTier;
  const maxResources = profile ? getMaxResources(subscriptionTier) : 0;
  const resourcesUsed = profile?.resources_used_this_month || 0;
  const canCreateMore = resourcesUsed < maxResources;
  const isInactive = profile?.billing_status === 'inactive';

  const onSubmit = async (data: ResourceFormData) => {
    if (isInactive) {
      toast({
        title: "Account Inactive",
        description: "Your account is currently inactive. Please update your billing information to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!canCreateMore) {
      toast({
        title: "Resource limit reached",
        description: "Please upgrade your plan to create more resources.",
        variant: "destructive",
      });
      return;
    }

    const success = await createResource(data);
    if (success) {
      reset();
      setIsOpen(false);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button disabled={isInactive}>
              <FilePlus className="mr-2 h-4 w-4" />
              Create Resource
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] w-[95vw] p-4 md:p-6">
            <DialogHeader>
              <DialogTitle className="text-xl">Create New Training Resource</DialogTitle>
            </DialogHeader>
            {isInactive && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  Your account is currently inactive. Please update your billing information to continue.
                </AlertDescription>
              </Alert>
            )}
            {!isInactive && !canCreateMore && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  You've reached your resource limit ({resourcesUsed}/{maxResources}).
                  Please upgrade your plan to create more resources.
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
              <ResourceFormFields register={register} />
              <Button 
                type="submit" 
                disabled={isLoading || !canCreateMore || isInactive} 
                className="w-full mt-3"
              >
                {isLoading ? "Creating..." : "Create Resource"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Button 
          variant="outline" 
          disabled={isInactive}
          onClick={() => setIsLibraryOpen(true)}
        >
          <LibraryBig className="mr-2 h-4 w-4" />
          Course Library
        </Button>
      </div>

      <CourseLibraryDialog 
        open={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
      />
    </>
  );
}
